import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, ChevronLeft } from 'lucide-react';
import { CoachButton, Eyebrow, ErrorNote } from '../components/primitives';
import { MethodOption, PlanOption } from '../components/pay-options';
import { coaching, fmtPeso, performGatewayHandoff, store } from '../lib/api';
import { isNativeShell } from '@mlm/ui/lib/native-shell';
import { tierLabel } from '../lib/pro';
import { qk, useEntitlement, useProfile, useTierLabels } from '../lib/queries';

/**
 * SC-43. Everything a member needs to decide, and one button that charges —
 * EXCEPT inside the packaged Android app, where there is no button at all.
 *
 * WHY: Google Play requires digital subscriptions bought inside an app to go
 * through Play Billing. Charging for Pro through the tenant's own gateway in
 * the APK is a policy breach that gets a listing rejected or pulled. The same
 * path was broken there anyway — a gateway handoff opens the phone's browser
 * and cannot return, because the app's origin (`app.<domain>`) is a virtual
 * WebView host and not a real website.
 *
 * So the app DESCRIBES the plan and sends the member to the WEBSITE to buy it
 * (the user's call, 2026-10-13: "we keep the link going to miraclesprotocol.com";
 * if Play review ever objects, the link comes off and the wall stays). The
 * link opens in the phone's browser, never in the WebView, and the plan
 * unlocks here on the next read of the entitlement; `resolveTier` never cared
 * where the money came from.
 *
 * WHY NO AUTO-RENEW: renewing by itself needs a slot and a funded wallet, and
 * a coach member has neither (slots come from the store's kit redemption).
 * Rather than show a toggle that almost nobody could switch on, each month is
 * bought on its own and buying again ADDS to what is left, so nobody loses
 * time by paying early.
 */
const INCLUDED = [
  'Unlimited-feeling coaching within fair use',
  'Personalized daily actions',
  'More than one goal at a time',
  'Journal reflections and written affirmations',
  'Vedic guidance and your full chart',
  'A streak freeze each month',
];

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: 'long' }) : null;

export function UpgradePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fromPaywall = params.get('from') === 'paywall';
  const profile = useProfile();
  const entitlement = useEntitlement();
  const labels = useTierLabels();
  const plans = useQuery({ queryKey: qk.plans, queryFn: coaching.plans });
  const methods = useQuery({ queryKey: qk.paymentMethods, queryFn: store.paymentMethods });

  const [planId, setPlanId] = useState<string | null>(null);
  const [methodCode, setMethodCode] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const payable = (methods.data?.methods ?? []).filter(
    (m) =>
      m.virtualEnabled &&
      m.currency === 'cash' &&
      m.settlement !== 'cod_manual' &&
      // A wallet needs a slot, which a coach member does not have. The server
      // sends the method anyway so the app can explain itself; there is
      // nothing to explain on a screen selling one thing, so it is hidden.
      (m.settlement !== 'wallet' || m.balance !== null),
  );
  const tier = entitlement.data?.tier ?? 'free';
  // What to open on: the plan or tier the link named, else the next tier up
  // for a member who already holds one, else the first plan on sale.
  const wantedPlan = params.get('plan');
  const wantedTier = params.get('tier');
  const suggested =
    plans.data?.find((p) => p.id === wantedPlan) ??
    plans.data?.find((p) => p.tier === wantedTier) ??
    (tier === 'pro' ? plans.data?.find((p) => p.tier === 'elite') : null) ??
    plans.data?.[0] ??
    null;
  const plan = plans.data?.find((p) => p.id === planId) ?? suggested;
  const planName = plan?.tierLabel ?? (plan ? labels[plan.tier] : labels.pro);
  const included = plan?.included?.length ? plan.included : INCLUDED;
  const method =
    payable.find((m) => m.code === methodCode) ?? (payable.length === 1 ? payable[0] : null);

  const buy = useMutation({
    mutationFn: async () => {
      const order = await store.createOrder({
        productId: plan!.productId,
        paymentMethodCode: method!.code,
      });
      if (method!.settlement === 'gateway') {
        performGatewayHandoff(await store.initiateGateway(order.id));
        // The browser is on its way to the gateway; leave the page as it is.
        return null;
      }
      return order.id;
    },
    onSuccess: (orderId) => {
      if (orderId) navigate(`/orders/${orderId}`, { replace: true });
    },
    onError: setError,
  });

  const until = fmtDate(entitlement.data?.endsAt ?? null);
  const onboarded = Boolean(profile.data?.profile.onboardingCompletedAt);
  const back = fromPaywall ? '/paywall' : onboarded ? '/profile' : '/blueprint';

  return (
    <div className="mx-auto w-full max-w-xl px-5 pb-16 pt-6 lg:max-w-3xl lg:pt-10">
      <button
        type="button"
        onClick={() => navigate(back)}
        className="eyebrow mb-6 inline-flex items-center gap-1 hover:text-ink"
      >
        <ChevronLeft className="h-3.5 w-3.5" aria-hidden /> Back
      </button>

      <Eyebrow className="mb-2">{planName}</Eyebrow>
      <h1 className="font-display text-[2rem] font-semibold leading-[1.1] text-ink lg:text-[2.5rem]">
        Turn intention into daily progress
      </h1>
      <p className="mt-3 text-[0.95rem] leading-relaxed text-ink/70 lg:max-w-prose">
        {planName} gives you guidance written around your own goal, the actions to match, and the
        tools to keep a consistent practice.
      </p>

      {tier !== 'free' && until && (
        <p className="mt-5 border border-[rgba(176,141,63,0.4)] px-4 py-3 text-[0.9rem] text-ink/85">
          You are on {tierLabel(tier, labels)} until {until}. Buying it again adds to that date
          rather than replacing it; a higher plan starts its own.
        </p>
      )}

      <ul className="mt-8 space-y-3">
        {included.map((line) => (
          <li key={line} className="flex items-start gap-3 text-[0.93rem] text-ink/85">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden />
            {line}
          </li>
        ))}
      </ul>

      {isNativeShell() ? (
        <div className="mt-10 space-y-3">
          <a
            href={`${window.location.origin.replace(/^https?:\/\/app\./, 'https://')}/upgrade`}
            target="_blank"
            rel="noopener"
            className="block"
          >
            <CoachButton kind="gold" block>
              Subscribe at miraclesprotocol.com
            </CoachButton>
          </a>
          <p className="eyebrow text-center">
            Paid on the website; everything here unlocks on its own once it is.
          </p>
        </div>
      ) : plans.isPending || methods.isPending ? (
        <p className="eyebrow mt-10">Loading…</p>
      ) : !plan ? (
        <p className="mt-10 text-[0.93rem] text-ink/70">
          No plan is on sale right now. Please check back soon.
        </p>
      ) : (
        <>
          {(plans.data?.length ?? 0) > 1 && (
            <section className="mt-10">
              <Eyebrow className="mb-3">Choose a plan</Eyebrow>
              <div className="space-y-2">
                {plans.data!.map((p) => (
                  <PlanOption
                    key={p.id}
                    plan={p}
                    selected={p.id === plan.id}
                    onSelect={() => setPlanId(p.id)}
                  />
                ))}
              </div>
            </section>
          )}

          <section className="mt-10">
            <Eyebrow className="mb-3">How would you like to pay?</Eyebrow>
            {payable.length === 0 ? (
              <p className="text-[0.93rem] text-ink/70">
                No payment method is available right now. Please check back soon.
              </p>
            ) : (
              <div className="space-y-2">
                {payable.map((m) => (
                  <MethodOption
                    key={m.code}
                    method={m}
                    selected={m.code === method?.code}
                    onSelect={() => setMethodCode(m.code)}
                  />
                ))}
              </div>
            )}
          </section>

          {error !== null && <ErrorNote error={error} />}

          <div className="mt-8 space-y-3">
            <CoachButton
              kind="gold"
              block
              disabled={!method || buy.isPending}
              onClick={() => {
                setError(null);
                buy.mutate();
              }}
            >
              {buy.isPending ? 'Starting…' : `Pay ${fmtPeso(plan.price)}`}
            </CoachButton>
            <p className="eyebrow text-center">
              {(plans.data ?? []).length > 1 ? plan.label : 'One month'}. Nothing renews by itself.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
